import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

// Google Consent Mode v2. The defaults (everything denied) and the GA4 tag live
// in index.html so they are in the <head> of every prerendered page, before
// gtag.js and adsbygoogle.js. This component only records the visitor's choice
// and sends the matching `consent update`.
const CONSENT_KEY = 'cookiePreferences';

const defaultPreferences = {
  necessary: true,
  analytics: false,
  advertising: false
};

// Older stored objects only have `analytics` (they came from the previous
// "Accept all" / "Reject non-essential" banner), so advertising follows it.
const normalizePreferences = (stored) => {
  const merged = { ...defaultPreferences, ...stored };
  if (typeof stored?.advertising !== 'boolean') merged.advertising = merged.analytics === true;
  return merged;
};

// True once the visitor has made a choice in the banner. Until then gtag holds
// hits for up to `wait_for_update` (500 ms, index.html) waiting for one.
export const hasConsentChoice = () => {
  try {
    return localStorage.getItem(CONSENT_KEY) !== null;
  } catch (e) {
    return false;
  }
};

export const consentSignals = (prefs) => ({
  analytics_storage: prefs.analytics ? 'granted' : 'denied',
  ad_storage: prefs.advertising ? 'granted' : 'denied',
  ad_user_data: prefs.advertising ? 'granted' : 'denied',
  ad_personalization: prefs.advertising ? 'granted' : 'denied'
});

const updateConsent = (prefs) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', consentSignals(prefs));
  }
};

function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [showOptions, setShowOptions] = useState(false);

  const applyPreferences = (nextPreferences) => {
    updateConsent(nextPreferences);
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(nextPreferences));
    } catch (e) {
      /* storage unavailable (private mode / blocked) */
    }
  };

  useEffect(() => {
    // The build-time prerender must not bake the banner into the static HTML:
    // this component renders nothing on the first client render, and the HTML
    // has to match that for hydration to succeed.
    if (window.__PRERENDER__) return undefined;

    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
    } catch (e) {
      stored = null;
    }
    if (stored) {
      const merged = normalizePreferences(stored);
      setPreferences(merged);
      // index.html already re-applied this choice synchronously; repeating it
      // is harmless and covers a stale/invalid stored object.
      updateConsent(merged);
    } else {
      setIsVisible(true);
    }

    const handleOpenPreferences = () => {
      setShowOptions(true);
      setIsVisible(true);
    };
    window.addEventListener('openConsentPreferences', handleOpenPreferences);
    return () => {
      window.removeEventListener('openConsentPreferences', handleOpenPreferences);
    };
  }, []);

  const handleAcceptAll = () => {
    const next = { ...defaultPreferences, analytics: true, advertising: true };
    setPreferences(next);
    applyPreferences(next);
    setIsVisible(false);
  };

  const handleReject = () => {
    const next = { ...defaultPreferences, analytics: false, advertising: false };
    setPreferences(next);
    applyPreferences(next);
    setIsVisible(false);
  };

  const handleSave = () => {
    applyPreferences(preferences);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-consent" role="dialog" aria-live="polite" aria-label="Cookie preferences">
      <div className="cookie-consent-content">
        <div className="cookie-consent-text">
          <h2>Cookies, briefly</h2>
          <p>
            We use cookies to run the site, understand usage and show the ads that keep these puzzles free.
            Change your mind anytime from the footer. <Link to="/privacy-policy">Privacy Policy</Link>
          </p>
        </div>
        {showOptions && (
          <div className="cookie-consent-options">
            <label className="cookie-toggle">
              <span>Necessary <small>Always on</small></span>
              <input type="checkbox" checked disabled />
            </label>
            <label className="cookie-toggle">
              <span>Analytics <small>Anonymous usage stats</small></span>
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={(event) => setPreferences((prev) => ({ ...prev, analytics: event.target.checked }))}
              />
            </label>
            <label className="cookie-toggle">
              <span>Advertising <small>Personalized ads</small></span>
              <input
                type="checkbox"
                checked={preferences.advertising}
                onChange={(event) => setPreferences((prev) => ({ ...prev, advertising: event.target.checked }))}
              />
            </label>
          </div>
        )}
        <div className="cookie-consent-actions">
          {showOptions ? (
            <button onClick={handleSave} className="cookie-button cookie-button--wide">
              Save preferences
            </button>
          ) : (
            <button onClick={() => setShowOptions(true)} className="cookie-button link">
              Customize
            </button>
          )}
          <button onClick={handleReject} className="cookie-button">
            Reject all
          </button>
          <button onClick={handleAcceptAll} className="cookie-button">
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
